'use client';

import { useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useReactions, useToggleReaction } from '@kit/chat/hooks';
import { Button } from '@kit/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@kit/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kit/ui/tooltip';
import { cn } from '@kit/ui/utils';

interface MessageReactionsProps {
  messageId: string;
  /**
   * BAL-580: in an org thread viewed from outside the org, every other
   * reactor is shown as the org, never as the member who reacted.
   */
  maskOtherReactorsAs?: string | null;
  currentUserId?: string | null;
}

// Quick reaction emojis
const REACTION_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '👏'];

export function MessageReactions({
  messageId,
  maskOtherReactorsAs,
  currentUserId,
}: MessageReactionsProps) {
  const { t } = useTranslation();
  const [showPicker, setShowPicker] = useState(false);
  const { reactionCounts, groupedReactions, isLoading } = useReactions({
    messageId,
  });
  const toggleMutation = useToggleReaction();

  const handleToggle = async (emoji: string) => {
    try {
      await toggleMutation.mutateAsync({ messageId, emoji });
      setShowPicker(false);
    } catch {
      // Error handling done in mutation
    }
  };

  // Get sorted reactions
  const sortedReactions = useMemo(() => {
    return Object.entries(reactionCounts)
      .filter(([_, count]) => count > 0)
      .sort((a, b) => b[1] - a[1]);
  }, [reactionCounts]);

  if (isLoading) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center gap-1">
      {/* Existing reactions */}
      {sortedReactions.map(([emoji, count]) => {
        const reactors = groupedReactions.get(emoji) || [];
        const names = reactors
          .map((r) => {
            if (maskOtherReactorsAs && r.user_id !== currentUserId) {
              return maskOtherReactorsAs;
            }
            const fn = r.user?.first_name || '';
            const ln = r.user?.last_name || '';
            return `${fn} ${ln}`.trim() || t('chat:reactions.someone');
          })
          .slice(0, 5);
        const remainingCount = reactors.length - 5;

        return (
          <TooltipProvider key={emoji}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className={cn(
                    'h-6 gap-1 px-2 text-xs',
                    toggleMutation.isPending && 'opacity-50',
                  )}
                  onClick={() => handleToggle(emoji)}
                  disabled={toggleMutation.isPending}
                >
                  <span>{emoji}</span>
                  <span className="font-medium">{count}</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  {names.join(', ')}
                  {remainingCount > 0 &&
                    t('chat:reactions.andMore', { count: remainingCount })}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      })}

      {/* Add reaction button */}
      <Popover open={showPicker} onOpenChange={setShowPicker}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 opacity-0 transition-opacity group-hover:opacity-100 hover:opacity-100"
          >
            <Plus className="h-3 w-3" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-2" align="start">
          <div className="flex gap-1">
            {REACTION_EMOJIS.map((emoji) => (
              <Button
                key={emoji}
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 text-lg"
                onClick={() => handleToggle(emoji)}
                disabled={toggleMutation.isPending}
              >
                {emoji}
              </Button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
